import { useState, forwardRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import {
  Container,
  Grid,
  Button,
  Snackbar,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
// import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DonutSmallIcon from "@mui/icons-material/DonutSmall";
import BadgeIcon from "@mui/icons-material/Badge";
import MuiAlert from "@mui/material/Alert";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import OnboardingDetail from "../components/OnboardingDetail";
import Labels from "../labels/Labels";

// Enhanced JSON formatter component with beautiful indentation
const JsonFormatter = ({ data, maxHeight = "200px", compact = false }) => {
  if (!data) return <span style={{ color: "#999" }}>null</span>;

  let jsonString;
  let parsedData;

  try {
    // If it's already a string, try to parse it first to validate
    if (typeof data === "string") {
      // Try to parse the string as JSON
      try {
        parsedData = JSON.parse(data);
      } catch (parseError) {
        // If it's not valid JSON, just use the string as is
        return (
          <span
            style={{
              color: "#333",
              fontFamily: "monospace",
              fontSize: compact ? "11px" : "12px",
            }}
          >
            {data}
          </span>
        );
      }
    } else {
      parsedData = data;
    }

    // For tooltip (non-compact), use enhanced formatting with more spacing
    if (!compact) {
      jsonString = JSON.stringify(parsedData, null, 4); // 4 spaces for better readability
    } else {
      jsonString = JSON.stringify(parsedData, null, 0); // Compact for grid cells
    }
  } catch (e) {
    // If it's not valid JSON, just display as string
    return (
      <span
        style={{
          color: "#333",
          fontFamily: "monospace",
          fontSize: compact ? "11px" : "12px",
        }}
      >
        {data.toString()}
      </span>
    );
  }

  return (
    <pre
      style={{
        backgroundColor: compact ? "#f6f8fa" : "#fafbfc",
        border: `1px solid ${compact ? "#d0d7de" : "#e1e4e8"}`,
        borderRadius: compact ? "4px" : "8px",
        padding: compact ? "8px" : "20px",
        overflow: "auto",
        maxHeight: maxHeight,
        fontSize: compact ? "11px" : "14px",
        fontFamily: compact
          ? "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, monospace"
          : "Menlo, Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
        margin: 0,
        whiteSpace: "pre",
        lineHeight: compact ? "1.3" : "1.6",
        color: "#24292e",
        tabSize: 4,
        // Enhanced styling for tooltip version
        ...(compact
          ? {}
          : {
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15)",
              letterSpacing: "0.025em",
              wordSpacing: "0.1em",
            }),
      }}
    >
      {jsonString}
    </pre>
  );
};

// JSON Tooltip component for hovering over request/response data
const JsonTooltip = ({ data, children, title }) => {
  if (!data) {
    return children;
  }

  const tooltipContent = (
    <Box
      sx={{
        maxWidth: "600px",
        maxHeight: "400px",
        overflow: "auto",
        padding: "8px",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: "bold",
          marginBottom: "8px",
          color: "#333",
        }}
      >
        {title}
      </Typography>
      <JsonFormatter data={data} maxHeight="350px" compact={false} />
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={500}
      leaveDelay={200}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "#ffffff",
            border: "1px solid #ccc",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            "& .MuiTooltip-arrow": {
              color: "#ffffff",
            },
          },
        },
      }}
    >
      <Box sx={{ cursor: "help", width: "100%", height: "100%" }}>
        {children}
      </Box>
    </Tooltip>
  );
};

export default function Onboardings() {
  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("onboardings.column.id", "ID"),
      width: 1,
      hide: true,
    },

    {
      field: "firstName",
      headerName: Labels.getLabel("onboardings.column.firstName", "First Name"),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.firstName}
          </Typography>
        );
      },
    },
    {
      field: "lastName",
      headerName: Labels.getLabel("onboardings.column.lastName", "Last Name"),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.lastName}
          </Typography>
        );
      },
    },
    {
      field: "email",
      headerName: Labels.getLabel("onboardings.column.email", "Email"),
      width: 180,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.email}
          </Typography>
        );
      },
    },
    {
      field: "dob",
      headerName: Labels.getLabel("onboardings.column.dob", "DOB"),
      width: 80,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.dob}
          </Typography>
        );
      },
    },
    {
      field: "address",
      headerName: Labels.getLabel("onboardings.column.address", "Address"),
      width: 300,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.address}
          </Typography>
        );
      },
    },
    {
      field: "phone",
      headerName: Labels.getLabel("onboardings.column.phone", "Phone"),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.phone}
          </Typography>
        );
      },
    },
    {
      field: "ssn",
      headerName: Labels.getLabel("onboardings.column.ssn", "SSN"),
      width: 90,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.ssn}
          </Typography>
        );
      },
    },
    {
      field: "duration",
      headerName: Labels.getLabel("onboardings.column.duration", "Duration"),
      width: 80,
    },

    {
      field: "action",
      width: 180,
      headerName: Labels.getLabel("onboardings.column.action", "Action"),
      renderCell: (params) => {
        return (
          // you will find row info in params
          <div
            style={{
              backgroundColor:
                params.row.status === "REVIEW_IN_PROGRESS"
                  ? "antiquewhite"
                  : "white",
            }}
          >
            {
              <Button style={{ minWidth: "10px" }}>
                <ModeEditIcon
                  style={{ color: "darkgoldenrod" }}
                  onClick={(e) => onEditButtonClick(e, params.row)}
                  variant="contained"
                />
              </Button>
            }
            <Button style={{ minWidth: "10px" }}>
              <DonutSmallIcon
                style={{
                  color:
                    params.row.status === "REVIEW_IN_PROGRESS"
                      ? "white"
                      : "orange",
                }}
                onClick={(e) => onMarkInProgressButtonClick(e, params.row)}
                variant="contained"
              />
            </Button>
            <Button style={{ minWidth: "10px" }}>
              <ThumbUpAltIcon
                style={{ color: "green" }}
                onClick={(e) => onConfirmButtonClick(e, params.row)}
                variant="contained"
              />
            </Button>
            <Button style={{ minWidth: "10px" }}>
              <DeleteIcon
                style={{ color: "red" }}
                onClick={(e) => onDiscardButtonClick(e, params.row)}
                variant="contained"
              />
            </Button>
            <Button style={{ minWidth: "10px" }}>
              <Tooltip title="View customer activity logs">
                <BadgeIcon
                  style={{ color: "purple" }}
                  onClick={(e) => onCustomerActivityButtonClick(e, params.row)}
                  variant="contained"
                />
              </Tooltip>
            </Button>
          </div>
        );
      },
    },
    {
      field: "creationDate",
      headerName: Labels.getLabel(
        "onboardings.column.creationDate",
        "Creation Date"
      ),
      width: 140,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.creationDate}
          </Typography>
        );
      },
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel(
        "onboardings.column.updateDate",
        "Update Date"
      ),
      width: 140,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.updateDate}
          </Typography>
        );
      },
    },
  ];

  const [customerIdentifier, setCustomerIdentifier] = useState();
  const [openEditDlg, setOpenEditDlg] = useState(false);

  // Activity dialog state
  const [activityDialog, setActivityDialog] = useState({
    open: false,
    loading: false,
    activities: [],
    totalCount: 0,
    pageSize: 25,
    hasMore: false,
    customerEmail: "",
    customerName: "",
  });

  const onEditButtonClick = (e, params) => {
    setCustomerIdentifier(params.email);
    setOpenEditDlg(true);
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    //props.setOpen(false);
  };

  const queryClient = useQueryClient();

  const handleSuccess = () => {
    queryClient.invalidateQueries(["onboardings", "onboardings-started"]);
    setFeedback({
      open: true,
      type: "success",
      message: Labels.getLabel(
        "onboardings.requestSent",
        "The request was sent"
      ),
      onClose: () => {
        //clearFunction();
        setFeedback({ open: false });
        handleClose();
      },
    });
  };

  const confirmOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/confirm`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const discardOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/discard`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const markInProgressOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/progress`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onConfirmButtonClick = (e, params) => {
    confirmOnboardingMutation.mutate({ email: params.email });
  };

  const onDiscardButtonClick = (e, params) => {
    discardOnboardingMutation.mutate({ email: params.email });
  };

  const onMarkInProgressButtonClick = (e, params) => {
    markInProgressOnboardingMutation.mutate({ email: params.email });
  };

  const onCustomerActivityButtonClick = async (e, params) => {
    try {
      if (!params?.email) {
        console.error("No email available for customer activity");
        return;
      }

      // Set loading state
      setActivityDialog((prev) => ({
        ...prev,
        loading: true,
        open: true,
        activities: [],
        customerEmail: params.email,
        customerName:
          `${params.firstName || ""} ${params.lastName || ""}`.trim() ||
          params.email,
      }));

      // Use the new API endpoint to get customer activity (using email as customer identifier)
      const url = `${window.API_URL}/member/activity?email=${encodeURIComponent(
        params.email
      )}&page=0&pageSize=50`;

      console.log("Fetching customer activity for email:", params.email);
      console.log("API URL:", url);

      // Make the actual API call
      const response = await HttpService.getAxiosClient().get(url);
      const data = response.data || {};

      console.log("Activity data:", data);

      // Update dialog with activities
      setActivityDialog({
        open: true,
        loading: false,
        activities: data.activities || [],
        customerEmail: params.email,
        customerName:
          `${params.firstName || ""} ${params.lastName || ""}`.trim() ||
          params.email,
        totalCount: data.totalCount || 0,
        page: data.page || 0,
        pageSize: data.pageSize || 50,
        hasMore: data.hasMore || false,
      });

      // Show feedback to user
      if (data.activities && data.activities.length > 0) {
        console.log(
          `Found ${data.activities.length} activity records for ${params.email}`
        );
      } else {
        console.log(`No activity records found for ${params.email}`);
      }
    } catch (error) {
      console.error("Error fetching customer activity:", error);

      // Update state to show error
      setActivityDialog({
        open: true,
        loading: false,
        activities: [],
        customerEmail: params?.email || "",
        customerName: params
          ? `${params.firstName || ""} ${params.lastName || ""}`.trim() ||
            params.email
          : "",
        totalCount: 0,
        page: 0,
        pageSize: 50,
        hasMore: false,
      });

      setFeedback({
        open: true,
        type: "error",
        message: "Error fetching member activity",
      });
    }
  };

  async function fetchOnboardings() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/onboarding/pending`
    );
    return response;
  }

  const { data, isLoading, isError } = useQuery(
    ["onboardings"],
    fetchOnboardings
  );

  return (
    <Container maxWidth="xlg">
      <br />
      <br />
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={12}>
          <h1>
            {Labels.getLabel("onboardings.title", "OnBoarding Applications")}
          </h1>

          {isLoading ? (
            <h4>{Labels.getLabel("onboardings.loading", "Loading...")}</h4>
          ) : !isError ? (
            <div style={{ height: 700, width: "100%" }}>
              <DataGrid
                rows={data?.data}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[5]}
              />
            </div>
          ) : (
            <h4>
              {Labels.getLabel(
                "onboardings.error",
                "An error ocurred, please try again later..."
              )}
            </h4>
          )}
        </Grid>
      </Grid>
      {openEditDlg ? (
        <OnboardingDetail
          action="SAVE"
          open={openEditDlg}
          setOpen={setOpenEditDlg}
          dialogTitle={Labels.getLabel(
            "onboardings.editDialogTitle",
            "Edit Onboarding"
          )}
          customerIdentifier={customerIdentifier}
        />
      ) : (
        ""
      )}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>

      {/* Activity Dialog */}
      <Dialog
        open={activityDialog.open}
        onClose={() => setActivityDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Customer Activity Logs
          {activityDialog.customerName && (
            <Typography variant="body2" color="textSecondary">
              Customer: {activityDialog.customerName} (
              {activityDialog.customerEmail})
            </Typography>
          )}
          {activityDialog.activities.length > 0 && (
            <Typography variant="body2" color="textSecondary">
              Found {activityDialog.activities.length} activity records (Total:{" "}
              {activityDialog.totalCount})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={activityDialog.activities.map((activity, index) => ({
                id: activity.id || index,
                ...activity,
              }))}
              columns={[
                {
                  field: "logTimestamp",
                  headerName: "Timestamp",
                  width: 160,
                  renderCell: (params) => {
                    const dateValue = params.value
                      ? new Date(params.value).toLocaleString()
                      : "-";

                    return (
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {dateValue}
                      </Typography>
                    );
                  },
                },
                {
                  field: "method",
                  headerName: "Method",
                  width: 80,
                  renderCell: (params) => (
                    <Chip
                      label={params.value || ""}
                      size="small"
                      color={
                        params.value === "GET"
                          ? "info"
                          : params.value === "POST"
                            ? "success"
                            : params.value === "PUT"
                              ? "warning"
                              : params.value === "DELETE"
                                ? "error"
                                : "default"
                      }
                      sx={{ fontSize: "0.65rem", height: "20px" }}
                    />
                  ),
                },
                {
                  field: "path",
                  headerName: "Path",
                  width: 250,
                  renderCell: (params) => (
                    <Typography
                      sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}
                    >
                      {params.value || "-"}
                    </Typography>
                  ),
                },
                {
                  field: "responseCode",
                  headerName: "Status",
                  width: 80,
                  renderCell: (params) => {
                    const code = params.value;
                    const getStatusColor = (status) => {
                      if (!status) return "default";
                      const statusCode = parseInt(status);
                      if (statusCode >= 200 && statusCode < 300)
                        return "success";
                      if (statusCode >= 300 && statusCode < 400) return "info";
                      if (statusCode >= 400 && statusCode < 500)
                        return "warning";
                      if (statusCode >= 500) return "error";
                      return "default";
                    };

                    return (
                      <Chip
                        label={code || ""}
                        size="small"
                        color={getStatusColor(code)}
                        sx={{ fontSize: "0.65rem", height: "20px" }}
                      />
                    );
                  },
                },
                {
                  field: "userId",
                  headerName: "User ID",
                  width: 200,
                  renderCell: (params) => (
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {params.value || "-"}
                    </Typography>
                  ),
                },
                {
                  field: "remoteIp",
                  headerName: "IP",
                  width: 120,
                  renderCell: (params) => (
                    <Typography
                      sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}
                    >
                      {params.value || "-"}
                    </Typography>
                  ),
                },
                {
                  field: "timeTaken",
                  headerName: "Time (ms)",
                  width: 100,
                  renderCell: (params) => (
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {params.value || "-"}
                    </Typography>
                  ),
                },
                {
                  field: "request",
                  headerName: "Request",
                  width: 200,
                  renderCell: (params) => {
                    const data = params.value;
                    if (!data)
                      return (
                        <Typography sx={{ fontSize: "0.75rem" }}>-</Typography>
                      );

                    return (
                      <JsonTooltip data={data} title="Request Data">
                        <Box
                          sx={{
                            maxHeight: "120px",
                            overflow: "hidden",
                            width: "100%",
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <JsonFormatter
                            data={data}
                            maxHeight="100px"
                            compact={true}
                          />
                        </Box>
                      </JsonTooltip>
                    );
                  },
                },
                {
                  field: "response",
                  headerName: "Response",
                  width: 300,
                  renderCell: (params) => {
                    const data = params.value;
                    if (!data)
                      return (
                        <Typography sx={{ fontSize: "0.75rem" }}>-</Typography>
                      );

                    return (
                      <JsonTooltip data={data} title="Response Data">
                        <Box
                          sx={{
                            maxHeight: "120px",
                            overflow: "hidden",
                            width: "100%",
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <JsonFormatter
                            data={data}
                            maxHeight="100px"
                            compact={true}
                          />
                        </Box>
                      </JsonTooltip>
                    );
                  },
                },
              ]}
              loading={activityDialog.loading}
              autoHeight={false}
              disableSelectionOnClick
              density="compact"
              pageSize={activityDialog.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageSizeChange={(newPageSize) => {
                // Handle page size change if needed
                console.log("Page size changed to:", newPageSize);
              }}
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  lineHeight: "normal",
                  padding: "8px",
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "none !important",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActivityDialog((prev) => ({ ...prev, open: false }))
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
